/* eslint-disable import/no-cycle */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import API from '../services';

const initialState = {
  isLoading: false,
  ratings: [],
  isSendingLectureRateLoading: false,
  isLectureAlreadyRated: false,
  lectureInformations: {},
  exerciseInformations: {},
  exerciseVerificationInformations: [],
  modulesStatsData: [],
  modulesTeacherCommentsStatsData: [],
  modulesExercisesStatsData: [],
  modulesAvgRating: 0,
  isModulesStatsLoading: false,
  sectionsStatsData: [],
  sectionsTeacherCommentsStatsData: {},
  sectionsExercisesStatsData: {},
  sectionsAvgRating: 0,
  isSectionsStatsLoading: false,
  lecturesStatsData: [],
  lecturesTeacherCommentsStatsData: {},
  lecturesExercisesStatsData: {},
  lecturesAvgRating: 0,
  isLecturesStatsLoading: false,
  singleLectureStatsData: {},
  singleLectureTeacherCommentsStatsData: {},
  singleLectureExercisesStatsData: {},
  isSingleLectureStatsLoading: false,
  subjectContentVersions: [],
  isSubjectContentVersionsLoading: false,
  subjectRatings: {
    data: [],
    total: 0,
  },
  ticketAnswersRatings: {
    data: [],
    total: 0,
  },
  subjectTeacherCommentsRatings: {
    data: [],
    total: 0,
  },
  subjectExercisesRatings: {
    data: [],
    total: 0,
  },
  isSubjectRatingsLoading: false,
  isTicketAnswersRatingsLoading: false,
  monthlyTicketAnswerRatingStats: [],
  isMonthlyTicketAnswerRatingStatsLoading: false,
  allSectionsStats: [],
  allSectionsTeacherCommentsStatsData: [],
  allSectionsExercisesStatsData: [],
  allSectionsAvgRating: 0,
  isAllSectionsStatsLoading: false,
  allLecturesStats: [],
  allExercisesTeacherCommentsStatsData: [],
  allExercisesStatsData: [],
  allLecturesAvgRating: 0,
  isAllLecturesStatsLoading: false,
  isTeacherCommentsRatingsLoading: false,
  teacherCommentsRatingsData: null,
  isExerciseVerificationRatingsLoading: false,
  exerciseVerificationRatingsData: null,
  exerciseVerificationRatingsCounts: null,
  isExerciseVerificationRatingsCountsLoading: false,
  teacherCommentsRatingsError: null,
  isPeriodicRatingsLoading: false,
  periodicRatingsData: null,
};

export const getAllRatings = createAsyncThunk(
  'ratings/getAllRatings',
  async ({ page, limit, search, isWithComment, selectRating, courseId, sortingType }) => {
    return API.RatingsService.getAllRatings(page, limit, search, isWithComment, selectRating, courseId, sortingType);
  },
);

export const postLectureRate = createAsyncThunk('ratings/postLectureRate', async payload => {
  return API.RatingsService.postLectureRate(payload);
});

export const postTeacherCommentRate = createAsyncThunk('ratings/postTeacherCommentRate', async payload => {
  return API.RatingsService.postTeacherCommentRate(payload);
});

export const postExerciseRate = createAsyncThunk('ratings/postExerciseRate', async payload => {
  return API.RatingsService.postExerciseRate(payload);
});

export const postExerciseVerificationRate = createAsyncThunk('ratings/postExerciseVerificationRate', async payload => {
  return API.RatingsService.postExerciseVerificationRate(payload);
});

export const getLectureInformations = createAsyncThunk('ratings/getLectureInformations', async subjectId => {
  return API.RatingsService.getLectureInformations(subjectId);
});

export const getExerciseInformations = createAsyncThunk('ratings/getExerciseInformations', async subjectId => {
  return API.RatingsService.getExerciseInformations(subjectId);
});

export const getExerciseVerificationInformations = createAsyncThunk(
  'ratings/getExerciseVerificationInformations',
  async subjectId => {
    return API.RatingsService.getExerciseVerificationInformations(subjectId);
  },
);

export const postTicketCommentRating = createAsyncThunk('ratings/postTicketCommentRating', async payload => {
  return API.RatingsService.postTicketCommentRating(payload);
});

export const getAllSectionsStats = createAsyncThunk(
  'ratings/getAllSectionsStats',
  async ({ courseId, fromDate, toDate }) => {
    return API.RatingsService.getAllSectionsStats({
      courseId,
      fromDate,
      toDate,
    });
  },
);

export const getAllSectionsTeacherCommentsStats = createAsyncThunk(
  'ratings/getAllSectionsTeacherCommentsStats',
  async ({ courseId, fromDate, toDate, teacherId }) => {
    return API.RatingsService.getAllSectionsTeacherCommentsStats({
      courseId,
      fromDate,
      toDate,
      teacherId,
    });
  },
);

export const getAllSectionsExercisesStats = createAsyncThunk(
  'ratings/getAllSectionsExercisesStats',
  async ({ courseId, fromDate, toDate }) => {
    return API.RatingsService.getAllSectionsExercisesStats({
      courseId,
      fromDate,
      toDate,
    });
  },
);

export const getAllLecturesStats = createAsyncThunk(
  'ratings/getAllLecturesStats',
  async ({ fromDate, toDate, courseId }) => {
    return API.RatingsService.getAllLecturesStats({
      fromDate,
      toDate,
      courseId,
    });
  },
);

export const getAllExercisesStats = createAsyncThunk(
  'ratings/getAllExercisesStats',
  async ({ fromDate, toDate, courseId }) => {
    return API.RatingsService.getAllExercisesStats({
      fromDate,
      toDate,
      courseId,
    });
  },
);

export const getAllTeacherCommentsRatings = createAsyncThunk('ratings/getAllTeacherCommentsRatings', async payload => {
  return API.RatingsService.getAllTeacherCommentsRatings(payload);
});

export const getAllExerciseVerificationRatings = createAsyncThunk(
  'ratings/getAllExerciseVerificationRatings',
  async payload => {
    return API.RatingsService.getAllExerciseVerificationRatings(payload);
  },
);

export const getExerciseVerificationRatingsCounts = createAsyncThunk(
  'ratings/getExerciseVerificationRatingsCounts',
  async ({ fromDate, toDate, courseId, teacherId, statusType }) => {
    return API.RatingsService.getExerciseVerificationRatingsCounts({
      fromDate,
      toDate,
      courseId,
      teacherId,
      statusType,
    });
  },
);

export const getTeacherCommentsPeriodicRatings = createAsyncThunk(
  'ratings/getTeacherCommentsPeriodicRatings',
  async payload => {
    return API.RatingsService.getTeacherCommentsPeriodicRatings(payload);
  },
);

export const getAllTeacherCommentsExercisesStats = createAsyncThunk(
  'ratings/getAllTeacherCommentsExercisesStats',
  async ({ fromDate, toDate, courseId, teacherId }) => {
    return API.RatingsService.getAllTeacherCommentsExercisesStats({
      fromDate,
      toDate,
      courseId,
      teacherId,
    });
  },
);

export const getModulesStats = createAsyncThunk('ratings/getModulesStats', async ({ courseId, fromDate, toDate }) => {
  return API.RatingsService.getModulesStats({
    courseId,
    fromDate,
    toDate,
  });
});

export const getTeacherCommentsModulesStats = createAsyncThunk(
  'ratings/getTeacherCommentsModulesStats',
  async ({ courseId, fromDate, toDate, teacherId }) => {
    return API.RatingsService.getTeacherCommentsModulesStats({
      courseId,
      fromDate,
      toDate,
      teacherId,
    });
  },
);

export const getExercisesModulesStats = createAsyncThunk(
  'ratings/getExercisesModulesStats',
  async ({ courseId, fromDate, toDate }) => {
    return API.RatingsService.getExercisesModulesStats({
      courseId,
      fromDate,
      toDate,
    });
  },
);

export const getExercisesSectionsStats = createAsyncThunk(
  'ratings/getExercisesSectionsStats',
  async ({ moduleId, fromDate, toDate }) => {
    return API.RatingsService.getExercisesSectionsStats({
      moduleId,
      fromDate,
      toDate,
    });
  },
);

export const getExercisesLecturesStats = createAsyncThunk(
  'ratings/getExercisesLecturesStats',
  async ({ sectionId, fromDate, toDate }) => {
    return API.RatingsService.getExercisesLecturesStats({
      sectionId,
      fromDate,
      toDate,
    });
  },
);

export const getTeacherCommentsSectionsStats = createAsyncThunk(
  'ratings/getTeacherCommentsSectionsStats',
  async ({ moduleId, fromDate, toDate, teacherId }) => {
    return API.RatingsService.getTeacherCommentsSectionsStats({
      moduleId,
      fromDate,
      toDate,
      teacherId,
    });
  },
);
export const getTeacherCommentsLecturesStats = createAsyncThunk(
  'ratings/getTeacherCommentsLecturesStats',
  async ({ sectionId, fromDate, toDate, teacherId }) => {
    return API.RatingsService.getTeacherCommentsLecturesStats({
      sectionId,
      fromDate,
      toDate,
      teacherId,
    });
  },
);

export const getSectionsStats = createAsyncThunk('ratings/getSectionsStats', async ({ moduleId, fromDate, toDate }) => {
  return API.RatingsService.getSectionsStats({
    moduleId,
    fromDate,
    toDate,
  });
});

export const getLecturesStats = createAsyncThunk(
  'ratings/getLecturesStats',
  async ({ sectionId, fromDate, toDate }) => {
    return API.RatingsService.getLecturesStats({
      sectionId,
      fromDate,
      toDate,
    });
  },
);

export const getSingleLectureStats = createAsyncThunk(
  'ratings/getSingleLectureStats',
  async ({ subjectId, fromDate, toDate, contentVersion, isWithComments }) => {
    return API.RatingsService.getSingleLectureStats({
      subjectId,
      fromDate,
      toDate,
      contentVersion,
      isWithComments,
    });
  },
);

export const getSingleLectureTeacherCommentStats = createAsyncThunk(
  'ratings/getSingleLectureTeacherCommentStats',
  async ({ subjectId, fromDate, toDate, contentVersion, teacherId }) => {
    return API.RatingsService.getSingleLectureTeacherCommentStats({
      subjectId,
      fromDate,
      toDate,
      contentVersion,
      teacherId,
    });
  },
);

export const getSingleLectureExercisesStats = createAsyncThunk(
  'ratings/getSingleLectureExercisesStats',
  async ({ subjectId, fromDate, toDate, contentVersion }) => {
    return API.RatingsService.getSingleLectureExercisesStats({
      subjectId,
      fromDate,
      toDate,
      contentVersion,
    });
  },
);

export const getSubjectContentVersions = createAsyncThunk('ratings/getSubjectContentVersions', async subjectId => {
  return API.RatingsService.getSubjectContentVersions(subjectId);
});

export const getSubjectRatings = createAsyncThunk(
  'ratings/getSubjectRatings',
  async ({ from, to, sortingType, page, limit, contentVersion, subjectId, isWithComments }) => {
    return API.RatingsService.getSubjectRatings({
      from,
      to,
      sortingType,
      page,
      limit,
      contentVersion,
      subjectId,
      isWithComments,
    });
  },
);

export const getTicketAnswersRatings = createAsyncThunk(
  'ratings/getTicketAnswersRatings',
  async ({ from, to, sortingType, page, limit, courseId, teacherId }) => {
    return API.RatingsService.getTicketAnswersRatings({
      from,
      to,
      sortingType,
      page,
      limit,
      courseId,
      teacherId,
    });
  },
);

export const getMonthlyTicketAnswerRatingStats = createAsyncThunk(
  'ratings/getMonthlyTicketAnswerRatingStats',
  async ({ year, courseId, teacherId }) => {
    return API.RatingsService.getMonthlyTicketAnswerRatingStats({
      year,
      courseId,
      teacherId,
    });
  },
);

export const getSubjectTeacherCommentsRatings = createAsyncThunk(
  'ratings/getSubjectTeacherCommentsRatings',
  async ({ from, to, sortingType, page, limit, contentVersion, subjectId, teacherId }) => {
    return API.RatingsService.getSubjectTeacherCommentsRatings({
      from,
      to,
      sortingType,
      page,
      limit,
      contentVersion,
      subjectId,
      teacherId,
    });
  },
);

export const getSubjectExercisesRatings = createAsyncThunk(
  'ratings/getSubjectExercisesRatings',
  async ({ from, to, sortingType, page, limit, contentVersion, subjectId }) => {
    return API.RatingsService.getSubjectExercisesRatings({
      from,
      to,
      sortingType,
      page,
      limit,
      contentVersion,
      subjectId,
    });
  },
);

const ratingsSlice = createSlice({
  name: 'ratings',
  initialState,
  reducers: {},
  extraReducers: {
    [getAllRatings.pending.toString()]: state => {
      state.isLoading = true;
    },
    [getAllRatings.rejected.toString()]: (state, action) => {
      state.isLoading = false;
      notification.error({
        message: 'Nie udało się pobrać ostrzeżeń',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getAllRatings.fulfilled.toString()]: (state, action) => {
      state.isLoading = false;
      state.ratings = action.payload.data;
    },
    [postLectureRate.pending.toString()]: state => {
      state.isSendingLectureRateLoading = true;
    },
    [postLectureRate.fulfilled.toString()]: state => {
      state.isSendingLectureRateLoading = false;
    },
    [postLectureRate.rejected.toString()]: (state, action) => {
      state.isSendingLectureRateLoading = false;
      notification.error({
        message: 'Opinia została już wysłana.',
        description: `${
          action.error.message === 'Rating already exists'
            ? 'Opinię można wystawić tylko jeden raz.'
            : action.error.message === 'Rating must be between 1 and 5'
            ? 'Opinia musi być w skali od 1 do 5'
            : action.error.message === 'Assigned lecture not found'
            ? 'Oceniany wykład nie został znaleziony'
            : action.error.message === 'Lecture not found'
            ? 'Wykład nie został znaleziony'
            : ''
        }`,
        placement: 'bottomRight',
      });
    },
    [postTeacherCommentRate.pending.toString()]: state => {
      state.isSendingLectureRateLoading = true;
    },
    [postTeacherCommentRate.fulfilled.toString()]: state => {
      state.isSendingLectureRateLoading = false;
    },
    [postTeacherCommentRate.rejected.toString()]: (state, action) => {
      state.isSendingLectureRateLoading = false;
      notification.error({
        message: 'Opinia została już wysłana.',
        description: `${
          action.error.message === 'Rating already exists'
            ? 'Opinię można wystawić tylko jeden raz.'
            : action.error.message === 'Rating must be between 1 and 5'
            ? 'Opinia musi być w skali od 1 do 5'
            : action.error.message === 'Assigned lecture not found'
            ? 'Oceniany komentarz nie został znaleziony'
            : action.error.message === 'Comment not found'
            ? 'Komentarz nie został znaleziony'
            : ''
        }`,
        placement: 'bottomRight',
      });
    },
    [getLectureInformations.pending.toString()]: state => {},
    [getLectureInformations.rejected.toString()]: (state, action) => {
      notification.error({
        message: 'Nie udało się pobrać informacji o wykładzie',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getLectureInformations.fulfilled.toString()]: (state, action) => {
      state.lectureInformations = action.payload.data;
    },
    [getExerciseInformations.pending.toString()]: state => {},
    [getExerciseInformations.rejected.toString()]: (state, action) => {
      notification.error({
        message: 'Nie udało się pobrać informacji o ćwiczeniu',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getExerciseInformations.fulfilled.toString()]: (state, action) => {
      state.exerciseInformations = action.payload.data;
    },
    [getExerciseVerificationInformations.pending.toString()]: state => {},
    [getExerciseVerificationInformations.rejected.toString()]: (state, action) => {
      notification.error({
        message: 'Nie udało się pobrać informacji o ćwiczeniu',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getExerciseVerificationInformations.fulfilled.toString()]: (state, action) => {
      state.exerciseVerificationInformations = action.payload.data;
    },
    [getModulesStats.pending.toString()]: state => {
      state.isModulesStatsLoading = true;
    },
    [getModulesStats.rejected.toString()]: (state, action) => {
      state.isModulesStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getModulesStats.fulfilled.toString()]: (state, action) => {
      state.isModulesStatsLoading = false;
      state.modulesStatsData = action.payload.data.modulesWithAvgRating;
      state.modulesAvgRating = action.payload.data.avgFromAllLectures;
    },
    [getSectionsStats.pending.toString()]: state => {
      state.isSectionsStatsLoading = true;
    },
    [getSectionsStats.rejected.toString()]: (state, action) => {
      state.isSectionsStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getSectionsStats.fulfilled.toString()]: (state, action) => {
      state.isSectionsStatsLoading = false;
      state.sectionsStatsData = action.payload.data;
      state.sectionsAvgRating = action.payload.data.avgFromAllLectures;
    },
    [getLecturesStats.pending.toString()]: state => {
      state.isLecturesStatsLoading = true;
    },
    [getLecturesStats.rejected.toString()]: (state, action) => {
      state.isLecturesStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getLecturesStats.fulfilled.toString()]: (state, action) => {
      state.isLecturesStatsLoading = false;
      state.lecturesStatsData = action.payload.data;
      state.lecturesAvgRating = action.payload.data.avgFromAllLectures;
    },
    [getSingleLectureStats.pending.toString()]: state => {
      state.isSingleLectureStatsLoading = true;
    },
    [getSingleLectureStats.rejected.toString()]: (state, action) => {
      state.isSingleLectureStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getSingleLectureStats.fulfilled.toString()]: (state, action) => {
      state.isSingleLectureStatsLoading = false;
      state.singleLectureStatsData = action.payload.data;
    },
    [getSubjectContentVersions.pending.toString()]: state => {
      state.isSubjectContentVersionsLoading = true;
    },
    [getSubjectContentVersions.rejected.toString()]: (state, action) => {
      state.isSubjectContentVersionsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać wersji treści',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getSubjectContentVersions.fulfilled.toString()]: (state, action) => {
      state.isSubjectContentVersionsLoading = false;
      state.subjectContentVersions = action.payload.data;
    },
    [getSubjectRatings.pending.toString()]: state => {
      state.isSubjectRatingsLoading = true;
    },
    [getSubjectRatings.rejected.toString()]: (state, action) => {
      state.isSubjectRatingsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getSubjectRatings.fulfilled.toString()]: (state, action) => {
      state.isSubjectRatingsLoading = false;
      state.subjectRatings = action.payload.data;
    },
    [getAllSectionsStats.pending.toString()]: state => {
      state.isAllSectionsStatsLoading = true;
    },
    [getAllSectionsStats.rejected.toString()]: (state, action) => {
      state.isAllSectionsStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getAllSectionsStats.fulfilled.toString()]: (state, action) => {
      state.isAllSectionsStatsLoading = false;
      state.allSectionsStats = action.payload.data.sectionsStats;
      state.allSectionsAvgRating = action.payload.data.avgFromAllLectures;
    },
    [getAllLecturesStats.pending.toString()]: state => {
      state.isAllLecturesStatsLoading = true;
    },
    [getAllLecturesStats.rejected.toString()]: (state, action) => {
      state.isAllLecturesStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getAllLecturesStats.fulfilled.toString()]: (state, action) => {
      state.isAllLecturesStatsLoading = false;
      state.allLecturesStats = action.payload.data.lecturesStats;
      state.allLecturesAvgRating = action.payload.data.avgFromAllLectures;
    },
    [getTeacherCommentsModulesStats.pending.toString()]: state => {
      state.isModulesStatsLoading = true;
    },
    [getTeacherCommentsModulesStats.rejected.toString()]: (state, action) => {
      state.isModulesStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getTeacherCommentsModulesStats.fulfilled.toString()]: (state, action) => {
      state.isModulesStatsLoading = false;
      state.modulesTeacherCommentsStatsData = action.payload.data;
    },
    [getTeacherCommentsSectionsStats.pending.toString()]: state => {
      state.isSectionsStatsLoading = true;
    },
    [getTeacherCommentsSectionsStats.rejected.toString()]: (state, action) => {
      state.isSectionsStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getTeacherCommentsSectionsStats.fulfilled.toString()]: (state, action) => {
      state.isSectionsStatsLoading = false;
      state.sectionsTeacherCommentsStatsData = action.payload.data;
    },
    [getTeacherCommentsLecturesStats.pending.toString()]: state => {
      state.isLecturesStatsLoading = true;
    },
    [getTeacherCommentsLecturesStats.rejected.toString()]: (state, action) => {
      state.isLecturesStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getTeacherCommentsLecturesStats.fulfilled.toString()]: (state, action) => {
      state.isLecturesStatsLoading = false;
      state.lecturesTeacherCommentsStatsData = action.payload.data;
    },
    [getSingleLectureTeacherCommentStats.pending.toString()]: state => {
      state.isSingleLectureStatsLoading = true;
    },
    [getSingleLectureTeacherCommentStats.rejected.toString()]: (state, action) => {
      state.isSingleLectureStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getSingleLectureTeacherCommentStats.fulfilled.toString()]: (state, action) => {
      state.isSingleLectureStatsLoading = false;
      state.singleLectureTeacherCommentsStatsData = action.payload.data;
    },
    [getSubjectTeacherCommentsRatings.pending.toString()]: state => {
      state.isSubjectRatingsLoading = true;
    },
    [getSubjectTeacherCommentsRatings.rejected.toString()]: (state, action) => {
      state.isSubjectRatingsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getSubjectTeacherCommentsRatings.fulfilled.toString()]: (state, action) => {
      state.isSubjectRatingsLoading = false;
      state.subjectTeacherCommentsRatings = action.payload.data;
    },
    [getAllSectionsTeacherCommentsStats.pending.toString()]: state => {
      state.isAllSectionsStatsLoading = true;
    },
    [getAllSectionsTeacherCommentsStats.rejected.toString()]: (state, action) => {
      state.isAllSectionsStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getAllSectionsTeacherCommentsStats.fulfilled.toString()]: (state, action) => {
      state.isAllSectionsStatsLoading = false;
      state.allSectionsTeacherCommentsStatsData = action.payload.data;
    },
    [getAllTeacherCommentsExercisesStats.pending.toString()]: state => {
      state.isAllLecturesStatsLoading = true;
    },
    [getAllTeacherCommentsExercisesStats.rejected.toString()]: (state, action) => {
      state.isAllLecturesStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getAllTeacherCommentsExercisesStats.fulfilled.toString()]: (state, action) => {
      state.isAllLecturesStatsLoading = false;
      state.allExercisesTeacherCommentsStatsData = action.payload.data;
    },
    [getAllTeacherCommentsRatings.pending.toString()]: state => {
      state.isTeacherCommentsRatingsLoading = true;
    },
    [getAllTeacherCommentsRatings.rejected.toString()]: (state, action) => {
      state.isTeacherCommentsRatingsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getAllTeacherCommentsRatings.fulfilled.toString()]: (state, action) => {
      state.isTeacherCommentsRatingsLoading = false;
      state.teacherCommentsRatingsData = action.payload.data;
    },
    [getTeacherCommentsPeriodicRatings.pending.toString()]: state => {
      state.isPeriodicRatingsLoading = true;
    },
    [getTeacherCommentsPeriodicRatings.rejected.toString()]: (state, action) => {
      state.isPeriodicRatingsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getTeacherCommentsPeriodicRatings.fulfilled.toString()]: (state, action) => {
      state.isPeriodicRatingsLoading = false;
      state.periodicRatingsData = action.payload.data;
    },
    [postExerciseRate.pending.toString()]: state => {
      state.isSendingLectureRateLoading = true;
    },
    [postExerciseRate.fulfilled.toString()]: state => {
      state.isSendingLectureRateLoading = false;
    },
    [postExerciseRate.rejected.toString()]: (state, action) => {
      state.isSendingLectureRateLoading = false;
      notification.error({
        message: 'Opinia została już wysłana.',
        description: `${
          action.error.message === 'Rating already exists'
            ? 'Opinię można wystawić tylko jeden raz.'
            : action.error.message === 'Rating must be between 1 and 5'
            ? 'Opinia musi być w skali od 1 do 5'
            : action.error.message === 'Assigned exercise not found'
            ? 'Oceniane zadanie nie został znaleziony'
            : action.error.message === 'Exercise not found'
            ? 'Zadanie nie zostało znalezione'
            : ''
        }`,
        placement: 'bottomRight',
      });
    },
    [postExerciseVerificationRate.pending.toString()]: state => {
      state.isSendingLectureRateLoading = true;
    },
    [postExerciseVerificationRate.fulfilled.toString()]: state => {
      state.isSendingLectureRateLoading = false;
    },
    [postExerciseVerificationRate.rejected.toString()]: (state, action) => {
      state.isSendingLectureRateLoading = false;
      notification.error({
        message: 'Opinia została już wysłana.',
        description: `${
          action.error.message === 'Rating already exists'
            ? 'Opinię można wystawić tylko jeden raz.'
            : action.error.message === 'Rating must be between 1 and 5'
            ? 'Opinia musi być w skali od 1 do 5'
            : action.error.message === 'Assigned exercise not found'
            ? 'Oceniane zadanie nie został znaleziony'
            : action.error.message === 'Exercise not found'
            ? 'Zadanie nie zostało znalezione'
            : ''
        }`,
        placement: 'bottomRight',
      });
    },
    [getExercisesModulesStats.pending.toString()]: state => {
      state.isModulesStatsLoading = true;
    },
    [getExercisesModulesStats.rejected.toString()]: (state, action) => {
      state.isModulesStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getExercisesModulesStats.fulfilled.toString()]: (state, action) => {
      state.isModulesStatsLoading = false;
      state.modulesExercisesStatsData = action.payload.data;
    },
    [getExercisesSectionsStats.pending.toString()]: state => {
      state.isSectionsStatsLoading = true;
    },
    [getExercisesSectionsStats.rejected.toString()]: (state, action) => {
      state.isSectionsStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getExercisesSectionsStats.fulfilled.toString()]: (state, action) => {
      state.isSectionsStatsLoading = false;
      state.sectionsExercisesStatsData = action.payload.data;
    },
    [getExercisesLecturesStats.pending.toString()]: state => {
      state.isLecturesStatsLoading = true;
    },
    [getExercisesLecturesStats.rejected.toString()]: (state, action) => {
      state.isLecturesStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getExercisesLecturesStats.fulfilled.toString()]: (state, action) => {
      state.isLecturesStatsLoading = false;
      state.lecturesExercisesStatsData = action.payload.data;
    },
    [getSubjectExercisesRatings.pending.toString()]: state => {
      state.isSubjectRatingsLoading = true;
    },
    [getSubjectExercisesRatings.rejected.toString()]: (state, action) => {
      state.isSubjectRatingsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getSubjectExercisesRatings.fulfilled.toString()]: (state, action) => {
      state.isSubjectRatingsLoading = false;
      state.subjectExercisesRatings = action.payload.data;
    },
    [getSingleLectureExercisesStats.pending.toString()]: state => {
      state.isSingleLectureStatsLoading = true;
    },
    [getSingleLectureExercisesStats.rejected.toString()]: (state, action) => {
      state.isSingleLectureStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getSingleLectureExercisesStats.fulfilled.toString()]: (state, action) => {
      state.isSingleLectureStatsLoading = false;
      state.singleLectureExercisesStatsData = action.payload.data;
    },
    [getAllSectionsExercisesStats.pending.toString()]: state => {
      state.isAllSectionsStatsLoading = true;
    },
    [getAllSectionsExercisesStats.rejected.toString()]: (state, action) => {
      state.isAllSectionsStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getAllSectionsExercisesStats.fulfilled.toString()]: (state, action) => {
      state.isAllSectionsStatsLoading = false;
      state.allSectionsExercisesStatsData = action.payload.data;
    },
    [getAllExercisesStats.pending.toString()]: state => {
      state.isAllLecturesStatsLoading = true;
    },
    [getAllExercisesStats.rejected.toString()]: (state, action) => {
      state.isAllLecturesStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getAllExercisesStats.fulfilled.toString()]: (state, action) => {
      state.isAllLecturesStatsLoading = false;
      state.allExercisesStatsData = action.payload.data;
    },
    [getAllExerciseVerificationRatings.pending.toString()]: state => {
      state.isExerciseVerificationRatingsLoading = true;
    },
    [getAllExerciseVerificationRatings.rejected.toString()]: (state, action) => {
      state.isExerciseVerificationRatingsLoading = false;
    },
    [postTicketCommentRating.pending.toString()]: state => {
      state.isSendingLectureRateLoading = true;
    },
    [postTicketCommentRating.fulfilled.toString()]: state => {
      state.isSendingLectureRateLoading = false;
      notification.success({
        message: 'Dziękujemy za przesłanie opinii!',
        description: 'Pamiętaj, że każda opinia pozwala nam tworzyć jeszcze lepsze materiały.',
        placement: 'bottomRight',
      });
    },
    [postTicketCommentRating.rejected.toString()]: (state, action) => {
      state.isSendingLectureRateLoading = false;
      notification.error({
        message: 'Opinia została już wysłana.',
        description: `${
          action.error.message === 'Rating already exists'
            ? 'Opinię można wystawić tylko jeden raz.'
            : action.error.message === 'Rating must be between 1 and 5'
            ? 'Opinia musi być w skali od 1 do 5'
            : action.error.message === 'Assigned ticket comment not found'
            ? 'Oceniany komentarz nie został znaleziony'
            : action.error.message === 'Ticket comment not found'
            ? 'Komentarz nie został znaleziony'
            : ''
        }`,
        placement: 'bottomRight',
      });
    },
    [getTicketAnswersRatings.pending.toString()]: state => {
      state.isTicketAnswersRatingsLoading = true;
    },
    [getTicketAnswersRatings.rejected.toString()]: (state, action) => {
      state.isTicketAnswersRatingsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getAllExerciseVerificationRatings.fulfilled.toString()]: (state, action) => {
      state.isExerciseVerificationRatingsLoading = false;
      state.exerciseVerificationRatingsData = action.payload.data;
    },
    [getExerciseVerificationRatingsCounts.pending.toString()]: state => {
      state.isExerciseVerificationRatingsCountsLoading = true;
    },
    [getExerciseVerificationRatingsCounts.rejected.toString()]: (state, action) => {
      state.isExerciseVerificationRatingsCountsLoading = false;
    },
    [getTicketAnswersRatings.fulfilled.toString()]: (state, action) => {
      state.isTicketAnswersRatingsLoading = false;
      state.ticketAnswersRatings = action.payload.data;
    },
    [getMonthlyTicketAnswerRatingStats.pending.toString()]: state => {
      state.isMonthlyTicketAnswerRatingStatsLoading = true;
    },
    [getMonthlyTicketAnswerRatingStats.rejected.toString()]: (state, action) => {
      state.isMonthlyTicketAnswerRatingStatsLoading = false;
      notification.error({
        message: 'Nie udało się pobrać statystyk',
        description: `${action.error.message}`,
        placement: 'bottomRight',
      });
    },
    [getExerciseVerificationRatingsCounts.fulfilled.toString()]: (state, action) => {
      state.isExerciseVerificationRatingsCountsLoading = false;
      state.exerciseVerificationRatingsCounts = action.payload.data;
    },
    [getMonthlyTicketAnswerRatingStats.fulfilled.toString()]: (state, action) => {
      state.isMonthlyTicketAnswerRatingStatsLoading = false;
      state.monthlyTicketAnswerRatingStats = action.payload.data;
    },
  },
});

export default ratingsSlice.reducer;
